import { useState, useEffect } from "react";
import { API } from "@/App";
import { Settings, LogOut, RefreshCw, Trash2, Database, ExternalLink } from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { clearAllCache, getLastRefreshed, getRelativeTime } from "@/lib/cache";

export default function SettingsModal({ open, onClose, onLogout }) {
  const [clearing, setClearing] = useState(false);
  const [authStatus, setAuthStatus] = useState(null);
  const [cacheInfo, setCacheInfo] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (open) {
      fetch(`${API}/company-news/auth`)
        .then(r => r.json())
        .then(d => setAuthStatus(d))
        .catch(() => {});
      
      // Get cache info
      const lastRefreshed = getLastRefreshed();
      setCacheInfo(lastRefreshed);
    }
  }, [open]);

  const handleClearCache = () => {
    clearAllCache();
    toast.success("Cache cleared successfully");
    setCacheInfo({ newsStories: null, companyNews: {} });
  };

  const handleLogout = async () => {
    setClearing(true);
    try {
      await fetch(`${API}/company-news/auth`, { method: "DELETE" });
      clearAllCache();
      toast.success("Signed out successfully");
      onClose();
      onLogout?.();
    } catch {
      toast.error("Failed to sign out");
    } finally {
      setClearing(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent 
        className="max-w-md"
        style={{ background: '#FFFFFF', border: '1px solid #DCDFE2' }}
        data-testid="settings-modal"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#0A0A0A]">
            <Settings size={18} />
            Settings
          </DialogTitle>
          <DialogDescription className="text-[#6B7280]">
            Manage your account and cache settings.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 mt-4">
          {/* Account Section */}
          <div>
            <h3 className="small-caps mb-3">Account</h3>
            {authStatus?.configured ? (
              <div 
                className="p-4 rounded-lg"
                style={{ background: '#F4F5F6', border: '1px solid #DCDFE2' }}
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#0A0A0A] rounded-full flex items-center justify-center text-white text-sm font-semibold">
                    {authStatus.username?.[0]?.toUpperCase() || 'U'}
                  </div>
                  <div>
                    <p className="text-sm font-medium text-[#0A0A0A]">
                      {authStatus.username || "Unknown User"}
                    </p>
                    <p className="text-xs text-[#6B7280]">Authenticated via OAuth</p>
                  </div>
                </div>
              </div>
            ) : (
              <div 
                className="p-4 rounded-lg text-center"
                style={{ background: '#A6344610', border: '1px solid #A63446' }}
              >
                <p className="text-sm text-[#A63446]">Not authenticated</p>
              </div>
            )}
          </div>

          {/* Cache Section */}
          <div>
            <h3 className="small-caps mb-3">Cache</h3>
            <div 
              className="p-4 rounded-lg space-y-3"
              style={{ background: '#F4F5F6', border: '1px solid #DCDFE2' }}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Database size={14} className="text-[#6B7280]" />
                  <span className="text-sm text-[#3A3A3A]">News Stories</span>
                </div>
                <span className="text-xs text-[#6B7280]">
                  {cacheInfo?.newsStories 
                    ? `Last: ${getRelativeTime(cacheInfo.newsStories)}`
                    : 'Not cached'
                  }
                </span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Database size={14} className="text-[#6B7280]" />
                  <span className="text-sm text-[#3A3A3A]">Company News</span>
                </div>
                <span className="text-xs text-[#6B7280]">
                  {Object.keys(cacheInfo?.companyNews || {}).length} companies cached
                </span>
              </div>
              <button
                onClick={handleClearCache}
                className="w-full flex items-center justify-center gap-2 h-9 rounded-lg text-sm text-[#6B7280] hover:bg-[#DCDFE2] transition-colors"
                style={{ border: '1px dashed #9AA0A6' }}
              >
                <Trash2 size={14} />
                Clear All Cache
              </button>
            </div>
          </div>

          {/* Portfolio Data Editor */}
          <div>
            <h3 className="small-caps mb-3">Data Management</h3>
            <button
              onClick={() => { onClose(); navigate("/settings/portfolio-data"); }}
              data-testid="settings-portfolio-data-link"
              className="w-full flex items-center justify-between gap-2 h-10 px-4 rounded-lg text-sm text-[#3A3A3A] hover:bg-[#F4F5F6] transition-colors"
              style={{ border: '1px solid #DCDFE2' }}
            >
              <div className="flex items-center gap-2">
                <Database size={14} className="text-[#6B7280]" />
                <span>Portfolio Data Editor</span>
              </div>
              <ExternalLink size={12} className="text-[#9AA0A6]" />
            </button>
          </div>

          {/* Sign Out */}
          <button
            onClick={handleLogout}
            disabled={clearing}
            data-testid="logout-btn"
            className="w-full h-10 rounded-lg text-sm font-medium text-[#A63446] hover:bg-[#A6344610] disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
            style={{ border: '1px solid #A63446' }}
          >
            <LogOut size={14} />
            {clearing ? "Signing out..." : "Sign Out"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
